import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import PageLayout from '../components/PageLayout';
import usersService from '../services/usersService';
import bookingsService from '../services/bookingsService';
import { useFormatCurrency } from '../config/useAppConfig';

const BookingCard = ({ 
  status, 
  refId, 
  title, 
  location, 
  checkIn, 
  checkOut, 
  price, 
  statusColor 
}) => (
  <div className={`bg-card rounded-2xl border-l-4 ${statusColor} border-y border-r border-border p-5 shadow-sm hover:shadow-md transition-all group`}>
    <div className="flex items-start justify-between mb-4">
      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
        status === 'confirmed' ? 'bg-tertiary/10 text-tertiary' : 
        status === 'pending' ? 'bg-primary/10 text-primary' : 
        status === 'completed' ? 'bg-muted text-muted-foreground' :
        'bg-destructive/10 text-destructive'
      }`}>
        {status}
      </span>
      <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest">Ref: #{refId}</p>
    </div>
    <h4 className="text-base font-bold mb-1 group-hover:text-primary transition-colors">{title}</h4>
    <p className="text-xs text-muted-foreground mb-4">{location}</p>
    <div className="flex items-center gap-4 py-3 border-y border-border mb-4">
      <div className="flex-1">
        <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest mb-1">Check In</p>
        <p className="text-xs font-bold">{checkIn}</p>
      </div>
      <Icon icon="lucide:arrow-right" className="text-muted-foreground" />
      <div className="flex-1 text-right">
        <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest mb-1">Check Out</p>
        <p className="text-xs font-bold">{checkOut}</p>
      </div>
    </div>
    <div className="flex items-center justify-between">
      <p className="text-lg font-bold">{price}</p>
      <Link 
        to={`/bookings/${refId}`}
        className="text-[10px] font-black uppercase tracking-widest text-primary hover:underline"
      >
        View Details
      </Link>
    </div>
  </div>
);

export default function GuestProfileOverview() {
  const [activeTab, setActiveTab] = useState('Bookings');
  const { id: userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const formatCurrency = useFormatCurrency();

  const handleSuspendToggle = async () => {
    if (suspendLoading) return;
    setSuspendLoading(true);
    try {
      await usersService.updateUserStatus(user.id);
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      setUser(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Failed to update user status:', err);
      alert(err.response?.data?.message || 'Failed to update user status');
    } finally {
      setSuspendLoading(false);
    }
  };

  // Fetch user from real API
  useEffect(() => {
    const fetchUser = async () => {
      console.log('GuestProfileOverview: Fetching user for ID:', userId);
      if (!userId || userId === 'undefined') {
        console.error('GuestProfileOverview: Invalid userId detected:', userId);
        setError('Invalid User ID');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      try {
        console.log('GuestProfileOverview: Calling usersService.fetchUserById...');
        const data = await usersService.fetchUserById(userId);
        console.log('GuestProfileOverview: Received user data:', data);
        setUser(data);
      } catch (err) {
        console.error('GuestProfileOverview: API Error:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load user');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  // Fetch bookings for this user from real API
  useEffect(() => {
    if (!user) return;
    const fetchBookings = async () => {
      setBookingsLoading(true);
      try {
        // Fetch all bookings filtered by userId
        const data = await bookingsService.fetchBookings({ userId });
        // data is an array from GET /api/bookings
        setUserBookings(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch user bookings:', err);
        setUserBookings([]);
      } finally {
        setBookingsLoading(false);
      }
    };
    fetchBookings();
  }, [user, userId]);

  // Calculate lifetime value from real bookings
  const lifetimeStats = React.useMemo(() => {
    // Count both confirmed and completed bookings as part of lifetime value
    const countsTowardsLTV = userBookings.filter(b => b.status === 'completed' || b.status === 'confirmed');
    const totalSpent = countsTowardsLTV.reduce((sum, b) => sum + parseFloat(b.totalAmount || 0), 0);
    const totalBookings = countsTowardsLTV.length;
    const avg = totalBookings > 0 ? totalSpent / totalBookings : 0;
    return { totalSpent, totalBookings, avg };
  }, [userBookings]);

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    );
  }

  if (error || !user) {
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <Icon icon="lucide:user-x" className="text-4xl text-muted-foreground" />
          <h2 className="text-xl font-bold">User Not Found</h2>
          <p className="text-muted-foreground">{error || "The user you're looking for doesn't exist."}</p>
          <Link to="/users" className="px-4 py-2 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition-all">
            Back to Users
          </Link>
        </div>
      </PageLayout>
    );
  }

  // User model fields are flat: firstName, lastName, email, phone, role, status, createdAt
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  const email = user.email || '';
  const phone = user.phone || '';
  const joinedDate = user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <PageLayout>
      {/* Header */}
      <header className="h-auto md:h-20 bg-card border-b border-border px-4 md:px-8 py-4 md:py-0 flex flex-col md:flex-row md:items-center justify-between shrink-0 gap-4">
        <div className="flex items-center gap-4">
          <Link to="/users" className="p-2 bg-muted rounded-xl hover:bg-primary/10 hover:text-primary transition-all active:scale-95">
            <Icon icon="lucide:arrow-left" className="text-xl" />
          </Link>
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-bold">Guest Profile</h1>
            <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-widest font-black">ID: {user.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <Link 
            to={`/users/edit/${user.id}`}
            className="flex-1 md:flex-none bg-muted text-foreground px-4 py-2.5 rounded-xl font-black flex items-center justify-center gap-2 hover:bg-muted/80 transition-all text-[10px] uppercase tracking-widest border border-border outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Icon icon="lucide:pencil" className="text-lg" />
            <span>Edit User</span>
          </Link>
          <button
            onClick={handleSuspendToggle}
            disabled={suspendLoading}
            className={`flex-1 md:flex-none px-5 py-2.5 rounded-xl font-black flex items-center justify-center gap-2 transition-all text-[10px] uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed ${
              user.status === 'active'
                ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                : 'bg-tertiary/10 text-tertiary hover:bg-tertiary/20'
            }`}
          >
            <Icon
              icon={user.status === 'active' ? 'lucide:shield-off' : 'lucide:shield-check'}
              className="text-lg"
            />
            <span>{suspendLoading ? 'Updating...' : user.status === 'active' ? 'Suspend User' : 'Reactivate User'}</span>
          </button>
        </div>
      </header>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-6 md:space-y-8 scroll-smooth">
        {/* Top Section: Identity & Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-2 bg-card rounded-2xl border border-border p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-8 shadow-sm">
            <div className="relative">
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-muted bg-muted flex items-center justify-center overflow-hidden shadow-xl">
                <Icon icon="lucide:user" className="text-6xl text-muted-foreground/40" />
              </div>
              <span className={`absolute bottom-2 right-2 w-8 h-8 ${
                user.status === 'active' ? 'bg-tertiary' : 'bg-muted-foreground'
              } border-4 border-card rounded-full flex items-center justify-center shadow-lg`}>
                <Icon icon="lucide:check" className="text-white text-xs" />
              </span>
            </div>
            <div className="flex-1 text-center md:text-left space-y-4">
              <div>
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-1">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold">{firstName} {lastName}</h2>
                  <span className={`inline-flex px-3 py-1 text-[10px] font-black rounded-lg uppercase tracking-wider self-center md:self-auto ${
                    user.role === 'host' ? 'bg-primary/10 text-primary' :
                    user.role === 'admin' ? 'bg-destructive/10 text-destructive' :
                    'bg-muted text-muted-foreground'
                  }`}>{user.role}</span>
                  <span className={`inline-flex px-3 py-1 text-[10px] font-black rounded-lg uppercase tracking-wider self-center md:self-auto ${
                    user.status === 'active' ? 'bg-tertiary/10 text-tertiary' : 'bg-muted text-muted-foreground'
                  }`}>{user.status}</span>
                </div>
                <p className="text-muted-foreground flex items-center justify-center md:justify-start gap-2 text-sm">
                  <Icon icon="lucide:mail" className="text-primary" />
                  {email}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t border-border">
                <div>
                  <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest mb-1">Phone</p>
                  <p className="text-sm font-bold">{phone || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest mb-1">Total Bookings</p>
                  <p className="text-sm font-bold">{userBookings.length}</p>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest mb-1">Joined</p>
                  <p className="text-sm font-bold">{joinedDate}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-card rounded-2xl border border-border shadow-sm p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon icon="lucide:zap" className="text-2xl text-primary" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest bg-primary/10 text-primary px-3 py-1 rounded-full">Lifetime Value</span>
            </div>
            <div>
              <h3 className="text-4xl font-bold mb-1 text-foreground">{formatCurrency(lifetimeStats.totalSpent)}</h3>
              <p className="text-muted-foreground text-sm font-medium">Across {lifetimeStats.totalBookings} completed bookings</p>
            </div>
            <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Avg. Booking Value</p>
                <p className="font-bold mt-1 text-foreground">{formatCurrency(lifetimeStats.avg)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Active Bookings</p>
                <p className="font-bold mt-1 text-foreground">
                  {userBookings.filter(b => b.status === 'confirmed' || b.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-1 bg-muted p-1 rounded-xl w-full md:w-auto">
              {['Bookings', 'Review Log'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 md:flex-none px-6 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${
                    activeTab === tab 
                      ? 'bg-card text-foreground shadow-sm border border-border' 
                      : 'text-muted-foreground hover:bg-card/50'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'Bookings' && (
            <div className="space-y-6">
              {bookingsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : userBookings.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {userBookings.map(booking => (
                    <BookingCard 
                      key={booking.id}
                      status={booking.status}
                      refId={booking.id}
                      title={booking.Property?.name || 'Property'}
                      location={booking.Property ? `${booking.Property.city || ''} ${booking.Property.country || ''}`.trim() : '—'}
                      checkIn={new Date(booking.checkInDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      checkOut={new Date(booking.checkOutDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      price={formatCurrency(parseFloat(booking.totalAmount || 0))}
                      statusColor={
                        booking.status === 'confirmed' ? 'border-tertiary' :
                        booking.status === 'pending' ? 'border-primary' :
                        booking.status === 'completed' ? 'border-muted-foreground' :
                        'border-destructive'
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon icon="lucide:calendar-x" className="text-4xl text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">No Bookings Found</h3>
                  <p className="text-muted-foreground">This user hasn't made any bookings yet.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'Review Log' && (
            <div className="space-y-4">
              <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
                <div className="p-6 border-b border-border">
                  <h3 className="text-lg font-heading font-bold">Guest Reviews</h3>
                  <p className="text-sm text-muted-foreground">Reviews left by {firstName} for properties</p>
                </div>
                <div className="text-center py-12">
                  <Icon icon="lucide:star-off" className="text-4xl text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">No Reviews Yet</h3>
                  <p className="text-muted-foreground">{firstName} hasn't left any property reviews yet.</p>
                </div>
              </div>
              
              <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
                <div className="p-6 border-b border-border">
                  <h3 className="text-lg font-heading font-bold">Host Reviews</h3>
                  <p className="text-sm text-muted-foreground">Reviews received from hosts</p>
                </div>
                <div className="text-center py-12">
                  <Icon icon="lucide:message-square-off" className="text-4xl text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">No Host Reviews</h3>
                  <p className="text-muted-foreground">No host reviews available for {firstName}.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
